import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ShuffleText from "../components/ShuffleText";
import { useTheme } from "../context/ThemeContext";

const S3_URL = "https://news.limkc.com/ai-news-summary.json"; // CloudFront-protected
const MARKETAUX_URL = "https://news.limkc.com/market-news.json"; // Marketaux financial news

const GUARDIAN_SECTIONS = {
  science: { url: "https://news.limkc.com/science-news.json", title: "Science Digest", description: "Latest discoveries and breakthroughs in science" },
  lifestyle: { url: "https://news.limkc.com/lifestyle-news.json", title: "Lifestyle & Wellness", description: "Tips and trends for better living" },
  food: { url: "https://news.limkc.com/food-news.json", title: "Food & Culinary", description: "Recipes, restaurants, and food culture" },
  travel: { url: "https://news.limkc.com/travel-news.json", title: "Travel Digest", description: "Destinations, tips, and travel inspiration" },
  education: { url: "https://news.limkc.com/education-news.json", title: "Education News", description: "Learning, schools, and educational trends" }
};

const Home = () => {
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(false);

  // Marketaux news states
  const [marketNews, setMarketNews] = useState([]);
  const [marketLoading, setMarketLoading] = useState(true);
  const [marketError, setMarketError] = useState(false);

  // Guardian news states
  const [guardianNews, setGuardianNews] = useState({
    science: [],
    lifestyle: [],
    food: [],
    travel: [],
    education: []
  });
  const [guardianLoading, setGuardianLoading] = useState(true);

  // Refs for section title and card animations
  const sectionRefs = useRef([]);
  const cardRefs = useRef([]);

  // Neural-net hero — pinned scrollytelling. The tall wrapper gives scroll
  // distance; scroll position 0..1 drives the sim's camera dolly (setScroll),
  // fades/zooms the WELCOME past, and zooms the real content (Today's Digest) up
  // out of the net's depth. Everything is scroll-position driven, so scrolling
  // back up rewinds the whole thing (sim + content zoom back out).
  const heroSimRef = useRef(null);
  const heroWrapRef = useRef(null);
  const heroContentRef = useRef(null);
  const digestRef = useRef(null);
  const digestSpacerRef = useRef(null);
  const digestPinRef = useRef(null);
  const { theme } = useTheme();
  useEffect(() => {
    try { heroSimRef.current?.contentWindow?.NeuralField?.setTheme(theme); } catch (e) {}
  }, [theme]);
  useEffect(() => {
    const cl = (v, a, b) => (v < a ? a : v > b ? b : v);
    let raf = 0;
    const apply = () => {
      raf = 0;
      const wrap = heroWrapRef.current;
      if (!wrap) return;
      const total = Math.max(1, wrap.offsetHeight - window.innerHeight);
      const p = cl(-wrap.getBoundingClientRect().top / total, 0, 1);
      try { heroSimRef.current?.contentWindow?.NeuralField?.setScroll?.(p); } catch (e) {}
      const sim = heroSimRef.current;
      if (sim) sim.style.opacity = String(cl(1 - (p - 0.9) / 0.1, 0, 1)); // net crossfades out over the last 10%, overlapping the digest emerging
      const content = heroContentRef.current;
      if (content) {
        content.style.opacity = String(cl(1 - p / 0.32, 0, 1)); // WELCOME fades out early
        content.style.transform = `scale(${1 + p * 1.6})`;      // ...and zooms past
      }
      // ── Stage 2: Today's Digest pins + zooms into your face ──────────────
      // The digest is PORTALED to <body> (fixed pins there; in-tree fixed doesn't —
      // that's why it snapped from the bottom before). A 200vh spacer in the flow
      // reserves the scroll distance. Scroll drives:
      //  • zoom: scale 0.3 -> 1 while centered (pointer-events:none = moves, no drag)
      //  • settle + scroll-away: 1:1 translate up so it leaves like normal content,
      //    interactive once settled. All scroll-position driven → reverses cleanly.
      const spacer = digestSpacerRef.current;
      const pin = digestPinRef.current;
      if (spacer && pin) {
        const vh = window.innerHeight || 1;
        const sTop = spacer.getBoundingClientRect().top;
        // FUSED emerge + zoom that OVERLAPS the hero end: the digest fades in AND
        // scales 0.7 -> 1 together, starting early (over the still-present net), so
        // it emerges out of the net rather than after it clears.
        const START = vh * 1.6; // begins ~hero 70%
        if (sTop >= START) {
          pin.style.transform = 'translate(-50%, -50%) scale(0.7)';
          pin.style.opacity = '0';
          pin.style.pointerEvents = 'none';
        } else if (sTop >= 0) {
          const f = cl((START - sTop) / START, 0, 1);
          pin.style.transform = `translate(-50%, -50%) scale(${0.7 + f * 0.3})`;
          pin.style.opacity = String(cl(f * 1.4, 0, 1));
          pin.style.pointerEvents = 'none';
        } else {
          // settled — scroll away 1:1 like normal content. pointer-events OFF so the
          // wheel passes through to the real scroller (scroll works both ways, and
          // the following sections come in). Carousel-click is handled separately.
          pin.style.transform = `translate(-50%, calc(-50% - ${-sTop}px)) scale(1)`;
          pin.style.opacity = '1';
          pin.style.pointerEvents = 'none';
        }
      }
    };
    // Digest clickability: ONLY when settled + idle. While scrolling the pin is
    // pointer-events:none so the wheel is never captured (no scroll-lock). The
    // wheel listener flips it to none on the first notch (wheel reaches window even
    // over an auto element), so resuming scroll unlocks instantly. After ~200ms of
    // no scroll/wheel, if the digest is settled and on-screen, it becomes clickable.
    let idleTimer = 0;
    const markScrolling = () => {
      const p = digestPinRef.current;
      if (p) p.style.pointerEvents = 'none';
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        const pp = digestPinRef.current, sp = digestSpacerRef.current;
        if (!pp || !sp) return;
        const vh = window.innerHeight || 1;
        const sTop = sp.getBoundingClientRect().top;
        if (sTop <= 0 && sTop > -vh * 0.85) pp.style.pointerEvents = 'auto'; // settled + on screen
      }, 200);
    };
    const onScroll = () => { markScrolling(); if (!raf) raf = requestAnimationFrame(apply); };
    // Capture phase on document catches scroll from ANY scroller — window OR the
    // inner overflow-auto container the app actually scrolls inside.
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('wheel', markScrolling, { passive: true });
    window.addEventListener('resize', onScroll);
    apply();
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('wheel', markScrolling);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(idleTimer);
    };
  }, []);

  // Carousel drag state
  const carouselRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Fetch News from S3 with Metadata Check (for AI Tech Digest section)
  const fetchNewsFromS3 = async () => {
    const cachedNews = localStorage.getItem("aiNews");
    const lastCacheTime = localStorage.getItem("lastMetadataCheck");
    const now = Date.now();
    const CACHE_EXPIRY = 12 * 60 * 60 * 1000; // 12 hours

    if (cachedNews && lastCacheTime && now - parseInt(lastCacheTime) < CACHE_EXPIRY) {
      try {
        const parsedData = JSON.parse(cachedNews);
        setNews(parsedData.articles || []);
        setNewsLoading(false);
        return;
      } catch (parseError) {
        console.error("Error parsing cached news:", parseError);
        localStorage.removeItem("aiNews");
      }
    }

    try {
      setNewsLoading(true);
      const response = await fetch(S3_URL, { method: "GET", cache: "no-store", mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch news");

      const data = await response.json();
      if (data.articles?.length > 0) {
        localStorage.setItem("aiNews", JSON.stringify(data));
        localStorage.setItem("lastMetadataCheck", now.toString());
        setNews(data.articles);
      }
      setNewsError(false);
    } catch (err) {
      console.error("Error fetching AI news:", err);
      setNewsError(true);
    } finally {
      setNewsLoading(false);
    }
  };

  // Fetch Marketaux News from S3
  const fetchMarketauxNews = async () => {
    const cachedNews = localStorage.getItem("marketNews");
    const lastCacheTime = localStorage.getItem("lastMarketNewsCheck");
    const now = Date.now();
    const CACHE_EXPIRY = 12 * 60 * 60 * 1000; // 12 hours

    if (cachedNews && lastCacheTime && now - parseInt(lastCacheTime) < CACHE_EXPIRY) {
      try {
        const parsedData = JSON.parse(cachedNews);
        setMarketNews(parsedData.articles || []);
        setMarketLoading(false);
        return;
      } catch (parseError) {
        console.error("Error parsing cached market news:", parseError);
        localStorage.removeItem("marketNews");
      }
    }

    try {
      setMarketLoading(true);
      const response = await fetch(MARKETAUX_URL, { method: "GET", cache: "no-store", mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch market news");

      const data = await response.json();
      if (data.articles?.length > 0) {
        localStorage.setItem("marketNews", JSON.stringify(data));
        localStorage.setItem("lastMarketNewsCheck", now.toString());
        setMarketNews(data.articles);
      }
      setMarketError(false);
    } catch (err) {
      console.error("Error fetching market news:", err);
      setMarketError(true);
    } finally {
      setMarketLoading(false);
    }
  };

  // Fetch Guardian News from S3
  const fetchGuardianNews = async () => {
    const CACHE_EXPIRY = 12 * 60 * 60 * 1000; // 12 hours
    const now = Date.now();

    try {
      setGuardianLoading(true);

      // Initialize with empty arrays to prevent undefined values
      const newGuardianNews = {
        science: [],
        lifestyle: [],
        food: [],
        travel: [],
        education: []
      };

      for (const [key, config] of Object.entries(GUARDIAN_SECTIONS)) {
        const cacheKey = `guardian_${key}`;
        const cachedData = localStorage.getItem(cacheKey);
        const lastCacheTime = localStorage.getItem(`${cacheKey}_time`);

        // Check cache first
        if (cachedData && lastCacheTime && now - parseInt(lastCacheTime) < CACHE_EXPIRY) {
          try {
            const parsedData = JSON.parse(cachedData);
            newGuardianNews[key] = parsedData.articles || [];
            if (process.env.NODE_ENV === 'development') {
              console.log(`[CACHE HIT] ${key}: ${newGuardianNews[key].length} articles`);
            }
            continue;
          } catch (parseError) {
            console.error(`Error parsing cached ${key}:`, parseError);
            localStorage.removeItem(cacheKey);
            localStorage.removeItem(`${cacheKey}_time`);
          }
        }

        // Fetch from S3
        if (process.env.NODE_ENV === 'development') {
          console.log(`[FETCHING] ${key} from ${config.url}`);
        }
        try {
          const response = await fetch(config.url, { method: "GET", cache: "no-store", mode: "cors" });
          if (response.ok) {
            const data = await response.json();
            if (data.articles?.length > 0) {
              localStorage.setItem(cacheKey, JSON.stringify(data));
              localStorage.setItem(`${cacheKey}_time`, now.toString());
              newGuardianNews[key] = data.articles;
              if (process.env.NODE_ENV === 'development') {
                console.log(`[FETCH SUCCESS] ${key}: ${data.articles.length} articles`);
              }
            } else {
              newGuardianNews[key] = [];
              if (process.env.NODE_ENV === 'development') {
                console.log(`[FETCH EMPTY] ${key}: No articles returned`);
              }
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.error(`[FETCH FAILED] ${key}: HTTP ${response.status}`);
            }
            newGuardianNews[key] = [];
          }
        } catch (fetchError) {
          console.error(`[FETCH ERROR] ${key}:`, fetchError);
          newGuardianNews[key] = [];
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[FINAL STATE]', newGuardianNews);
      }
      setGuardianNews(newGuardianNews);
    } catch (err) {
      console.error("Error fetching Guardian news:", err);
    } finally {
      setGuardianLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsFromS3();
    fetchMarketauxNews();
    fetchGuardianNews();
  }, []);

  // Debug logging for Guardian news loading (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[DEBUG] Guardian loading state:', guardianLoading);
      console.log('[DEBUG] Guardian news data:', {
        science: guardianNews.science?.length || 0,
        lifestyle: guardianNews.lifestyle?.length || 0,
        food: guardianNews.food?.length || 0,
        travel: guardianNews.travel?.length || 0,
        education: guardianNews.education?.length || 0
      });
    }
  }, [guardianLoading, guardianNews]);

  // Intersection Observer for section title and card animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in-view');
          }
        });
      },
      { threshold: 0.2 } // Trigger when 20% of element is visible
    );

    // Copy refs to variables to avoid stale closures in cleanup
    const currentSectionRefs = sectionRefs.current;
    const currentCardRefs = cardRefs.current;

    // Observe section titles
    currentSectionRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    // Observe cards
    currentCardRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentSectionRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
      currentCardRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [news, guardianNews]); // Re-run when news OR guardianNews data changes

  // Carousel drag handlers
  const handleMouseDown = (e) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
    carouselRef.current.style.cursor = 'grabbing';
    // Pause auto-scroll animation
    const track = carouselRef.current.querySelector('.carousel-track');
    if (track) track.style.animationPlayState = 'paused';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!carouselRef.current) return;
    setIsDragging(false);
    carouselRef.current.style.cursor = 'grab';
    // Resume auto-scroll animation
    const track = carouselRef.current.querySelector('.carousel-track');
    if (track) track.style.animationPlayState = 'running';
  };

  const handleMouseLeave = () => {
    if (!carouselRef.current) return;
    setIsDragging(false);
    carouselRef.current.style.cursor = 'grab';
    // Resume auto-scroll animation
    const track = carouselRef.current.querySelector('.carousel-track');
    if (track) track.style.animationPlayState = 'running';
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
    // Pause auto-scroll animation
    const track = carouselRef.current.querySelector('.carousel-track');
    if (track) track.style.animationPlayState = 'paused';
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !carouselRef.current) return;
    const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    if (!carouselRef.current) return;
    setIsDragging(false);
    // Resume auto-scroll animation
    const track = carouselRef.current.querySelector('.carousel-track');
    if (track) track.style.animationPlayState = 'running';
  };

  // Generate comprehensive structured data for SEO
  const generateStructuredData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "LimKC",
      "url": "https://www.limkc.com",
      "jobTitle": "Cloud Architect & AI Engineer",
      "description": "Expert in Cloud Architecture, AI Automation, Serverless Computing, and Multi-Cloud Infrastructure. Specializing in AWS, Kubernetes, and AI-driven solutions.",
      "knowsAbout": [
        "Cloud Architecture",
        "AI Automation",
        "AWS Lambda",
        "Kubernetes",
        "Serverless Computing",
        "Multi-Cloud Infrastructure",
        "DevOps",
        "IoT",
        "Machine Learning",
        "DynamoDB",
        "API Gateway"
      ],
      "sameAs": [
        "https://github.com/limkc",
        "https://www.linkedin.com/in/limkc"
      ],
      "alumniOf": {
        "@type": "Organization",
        "name": "Cloud Computing & AI Engineering"
      }
    };
  };

  // Generate video structured data for hero video
  const generateVideoStructuredData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": "Cloud Infrastructure & AI Automation Background",
      "description": "Background video showcasing cloud architecture, AI automation, and serverless computing expertise",
      "thumbnailUrl": "https://www.limkc.com/SocialMedia.png",
      "uploadDate": "2025-01-01T00:00:00Z",
      "contentUrl": "https://static.limkc.com/hero-video.mp4",
      "embedUrl": "https://www.limkc.com"
    };
  };

  return (
    <>
      {/* SEO Metadata - Comprehensive */}
      <Helmet>
        <title>limkc - Cloud Architect & AI Engineer | AWS, Kubernetes, Serverless Expert</title>
        <meta name="description" content="Professional Cloud Architect specializing in AI-driven automation, serverless computing, and multi-cloud infrastructure. Expert in AWS Lambda, Kubernetes, DynamoDB, and scalable cloud solutions." />
        <meta name="keywords" content="Cloud Architect, AI Engineer, AWS Expert, Kubernetes, Serverless, Lambda Functions, DynamoDB, Multi-Cloud, DevOps, AI Automation, IoT, Machine Learning, Cloud Infrastructure, API Gateway" />

        {/* Open Graph */}
        <meta property="og:title" content="LimKC - Cloud Architect & AI Engineer" />
        <meta property="og:description" content="Expert in Cloud Architecture, AI Automation, and Serverless Computing. Building scalable, AI-driven solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.limkc.com" />
        <meta property="og:image" content="https://www.limkc.com/SocialMedia.png" />
        <meta property="og:site_name" content="LimKC" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LimKC - Cloud Architect & AI Engineer" />
        <meta name="twitter:description" content="Expert in Cloud Architecture, AI Automation, and Serverless Computing." />
        <meta name="twitter:image" content="https://www.limkc.com/SocialMedia.png" />

        {/* Additional SEO */}
        <meta name="author" content="LimKC" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.limkc.com" />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(generateStructuredData())}</script>
        <script type="application/ld+json">{JSON.stringify(generateVideoStructuredData())}</script>
      </Helmet>

      <div className="w-full h-full overflow-auto">
        {/* HERO — the 300vh spacer only provides scroll distance; the actual sim +
            WELCOME are PORTALED to <body> below (position:fixed there pins to the
            viewport regardless of the app's nested scroll containers). */}
        <div ref={heroWrapRef} style={{ position: 'relative', height: '300vh' }} />
        {createPortal(
          <>
            <iframe
              ref={heroSimRef}
              src="/neural-net-sim.html"
              title="Neural network synapse field"
              scrolling="no"
              style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', border: 'none', pointerEvents: 'none', zIndex: 40, willChange: 'opacity' }}
              onLoad={() => { try { heroSimRef.current?.contentWindow?.NeuralField?.setTheme(theme); } catch (e) {} }}
            />
            {/* pointer-events:none on the whole overlay so the wheel passes THROUGH
                to the real scroller behind (this is a <body> portal child; otherwise
                it'd try to scroll window and the page would be stuck). Only the CTA
                re-enables pointer events. */}
            <div
              ref={heroContentRef}
              style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 41, pointerEvents: 'none', willChange: 'opacity, transform' }}
            >
              {/* centered + theme-aware color (var --text-primary = black in light,
                  white in dark) — the sim backdrop flips with the theme too */}
              <div className="hero-content" style={{ textAlign: 'center', color: 'var(--text-primary)' }}>
                <h1 className="hero-title" data-framer-appear-id="ebw5ya" style={{ color: 'var(--text-primary)' }}>
                  <ShuffleText text="WELCOME" duration={2000} shuffleSpeed={50} startDelay={800} />
                </h1>
                <p className="hero-subtitle hero-subtitle-animated" style={{ color: 'var(--text-primary)' }}>
                  <span className="word-blur-reveal">TO</span>
                </p>
                <p className="hero-subtitle hero-subtitle-animated" style={{ marginTop: '0.5rem', letterSpacing: '0.2em', color: 'var(--text-primary)' }}>
                  <span className="word-blur-reveal">EXPLORE</span>{' '}
                  <span className="word-blur-reveal">·</span>{' '}
                  <span className="word-blur-reveal">LEARN</span>{' '}
                  <span className="word-blur-reveal">·</span>{' '}
                  <span className="word-blur-reveal">BUILD</span>{' '}
                  <span className="word-blur-reveal">·</span>{' '}
                  <span className="word-blur-reveal">SHARE</span>
                </p>
              </div>
              {/* CTA back at the bottom, centered — original layout */}
              <div className="hero-actions" data-framer-appear-id="wso7ab" style={{ position: 'absolute', bottom: '2rem', left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'auto' }}>
                <Link to="/chatty">
                  <button className="btn-3d btn-lg btn-rainbow">Chat with Chatty</button>
                </Link>
              </div>
            </div>
          </>,
          document.body
        )}

        {/* STAGE 2 — Today's Digest pins + zooms into your face, then releases.
            The spacer reserves the scroll distance; the digest is PORTALED to
            <body> so position:fixed actually pins (in-tree fixed doesn't here). */}
        <div ref={digestSpacerRef} style={{ height: '100vh' }} />
        {createPortal(
          <div ref={digestPinRef} style={{ position: 'fixed', top: '50%', left: '50%', width: '100%', zIndex: 45, pointerEvents: 'none', willChange: 'transform, opacity' }}>
        <section ref={digestRef} className="section" style={{ transformOrigin: 'center center' }}>
          <div className="section-container">
            <div className="section-header">
              <h2 className="section-title">Today's Digest</h2>
              <p className="section-description">
                Stay updated with the latest AI and cloud computing news
              </p>
            </div>

            {(newsLoading || marketLoading || guardianLoading) ? (
              /* Skeleton for carousel */
              <div className="flex gap-4 overflow-hidden">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 w-[280px] rounded-xl animate-pulse"
                    style={{ backgroundColor: 'var(--bg-secondary)' }}
                  >
                    <div className="h-40 rounded-t-xl" style={{ backgroundColor: 'var(--glass-input-bg)' }} />
                    <div className="p-4">
                      <div className="h-4 w-16 rounded mb-3" style={{ backgroundColor: 'var(--glass-input-bg)' }} />
                      <div className="h-5 w-full rounded mb-2" style={{ backgroundColor: 'var(--glass-input-bg)' }} />
                      <div className="h-5 w-3/4 rounded mb-3" style={{ backgroundColor: 'var(--glass-input-bg)' }} />
                      <div className="h-3 w-full rounded mb-1" style={{ backgroundColor: 'var(--glass-input-bg)' }} />
                      <div className="h-3 w-5/6 rounded" style={{ backgroundColor: 'var(--glass-input-bg)' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="carousel-container"
                ref={carouselRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ cursor: 'grab', overflowX: 'scroll', scrollBehavior: 'auto' }}
              >
                <div className="carousel-track">
                {/* AI Tech Digest - First Article */}
                {news.length > 0 && (
                  <a href="#ai-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={news[0].featuredImage || '/ai.png'}
                        alt={news[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/ai.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">AI Tech</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{news[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{news[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Market News - First Article */}
                {marketNews.length > 0 && (
                  <a href="#market-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={marketNews[0].featuredImage || '/cloud.png'}
                        alt={marketNews[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Markets</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{marketNews[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{marketNews[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Science - First Article */}
                {guardianNews.science.length > 0 && (
                  <a href="#science-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.science[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.science[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Science</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.science[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.science[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Lifestyle - First Article */}
                {guardianNews.lifestyle.length > 0 && (
                  <a href="#lifestyle-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.lifestyle[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.lifestyle[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Lifestyle</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.lifestyle[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.lifestyle[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Food - First Article */}
                {guardianNews.food.length > 0 && (
                  <a href="#food-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.food[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.food[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Food</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.food[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.food[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Travel - First Article */}
                {guardianNews.travel.length > 0 && (
                  <a href="#travel-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.travel[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.travel[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Travel</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.travel[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.travel[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Education - First Article */}
                {guardianNews.education.length > 0 && (
                  <a href="#education-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.education[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.education[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Education</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.education[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.education[0].summary}</p>
                    </div>
                  </a>
                )}

                {/* Duplicate set for seamless loop */}
                {news.length > 0 && (
                  <a href="#ai-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={news[0].featuredImage || '/ai.png'}
                        alt={news[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/ai.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">AI Tech</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{news[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{news[0].summary}</p>
                    </div>
                  </a>
                )}

                {marketNews.length > 0 && (
                  <a href="#market-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={marketNews[0].featuredImage || '/cloud.png'}
                        alt={marketNews[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Markets</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{marketNews[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{marketNews[0].summary}</p>
                    </div>
                  </a>
                )}

                {guardianNews.science.length > 0 && (
                  <a href="#science-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.science[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.science[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Science</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.science[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.science[0].summary}</p>
                    </div>
                  </a>
                )}

                {guardianNews.lifestyle.length > 0 && (
                  <a href="#lifestyle-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.lifestyle[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.lifestyle[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Lifestyle</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.lifestyle[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.lifestyle[0].summary}</p>
                    </div>
                  </a>
                )}

                {guardianNews.food.length > 0 && (
                  <a href="#food-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.food[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.food[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Food</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.food[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.food[0].summary}</p>
                    </div>
                  </a>
                )}

                {guardianNews.travel.length > 0 && (
                  <a href="#travel-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.travel[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.travel[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Travel</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.travel[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.travel[0].summary}</p>
                    </div>
                  </a>
                )}

                {guardianNews.education.length > 0 && (
                  <a href="#education-news" className="card-glass carousel-card featured-card featured-card-link">
                    <div className="featured-card-img-container">
                      <img
                        src={guardianNews.education[0].featuredImage || '/cloud.png'}
                        alt={guardianNews.education[0].title}
                        className="featured-card-img"
                        onError={(e) => { e.target.src = '/cloud.png'; }}
                      />
                    </div>
                    <div className="p-md">
                      <div className="tag mb-sm">Education</div>
                      <h4 className="font-bold mb-sm line-clamp-2">{guardianNews.education[0].title}</h4>
                      <p className="text-secondary text-sm line-clamp-3">{guardianNews.education[0].summary}</p>
                    </div>
                  </a>
                )}
              </div>

                {/* Blur fade edges */}
                <div className="carousel-fade-left"></div>
                <div className="carousel-fade-right"></div>
              </div>
            )}
          </div>
        </section>
          </div>,
          document.body
        )}

        {/* AI TECH DIGEST SECTION - Preserved from original */}
        <section id="ai-news" className="section">
          <div className="section-container">
            <div className="section-header section-title-slide-left" ref={(el) => (sectionRefs.current[3] = el)}>
              <h2 className="section-title">AI Tech Digest</h2>
              <p className="section-description">
                Stay updated with the latest AI and cloud computing news
              </p>
            </div>

            <div className="mt-8">
              {newsLoading ? (
                /* Skeleton for AI Tech articles */
                <div className="grid grid-cols-1 gap-lg animate-pulse">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="card-3d-flat">
                      <div className="h-6 w-3/4 rounded mb-4" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-[280px] h-48 rounded-lg flex-shrink-0" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                        <div className="flex-1">
                          <div className="h-4 w-full rounded mb-2" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                          <div className="h-4 w-full rounded mb-2" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                          <div className="h-4 w-5/6 rounded mb-4" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                          <div className="h-3 w-32 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : newsError ? (
                <p className="text-center" style={{ color: 'var(--text-secondary)' }}>
                  Stay tuned for the next exciting news update!
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-lg">
                  {news.length === 0 ? (
                    <p className="text-center" style={{ color: 'var(--text-secondary)' }}>
                      No AI news available right now.
                    </p>
                  ) : (
                    news.slice(0, 6).map((article, index) => (
                      <div key={`article-${index}`} className="card-3d-flat card-slide-up" ref={(el) => (cardRefs.current[16 + index] = el)}>

                        {/* Title First */}
                        <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-accent-blue)' }}>
                          {article.title}
                        </h3>

                        {/* Featured Image */}
                        {article.featuredImage && (
                          <div className="overflow-hidden rounded-lg bg-gray-100 mb-4 w-full md:w-auto md:float-left md:max-w-[280px] md:mr-4 md:mb-2">
                            <img
                              src={article.featuredImage}
                              alt={article.title}
                              className="w-full h-auto object-contain hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        )}

                        {/* Summary wraps around image */}
                        <p className="text-secondary mb-md">
                          {article.summary || "No summary available."}
                        </p>

                        <div style={{ clear: 'both' }}></div>

                        {/* Source at Bottom */}
                        <p className="text-xs" style={{ color: '#9CA3AF' }}>
                          Source:{" "}
                          <a
                            href={article.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                            style={{ color: '#9CA3AF' }}
                          >
                            {article.sourceTitle}
                          </a>
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* MARKET NEWS SECTION */}
        <section id="market-news" className="section">
          <div className="section-container">
            <div className="section-header section-title-slide-right" ref={(el) => (sectionRefs.current[22] = el)}>
              <h2 className="section-title">Market Watch</h2>
              <p className="section-description">
                Trending financial news, market movements, and economic insights
              </p>
            </div>

            <div className="mt-8">
              {marketLoading ? (
                /* Skeleton for Market articles */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg animate-pulse">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="card-3d-flat">
                      <div className="h-5 w-4/5 rounded mb-3" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                      <div className="h-48 w-full rounded-lg mb-3" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                      <div className="h-3 w-full rounded mb-2" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                      <div className="h-3 w-5/6 rounded mb-4" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                      <div className="h-3 w-24 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    </div>
                  ))}
                </div>
              ) : marketError ? (
                <p className="text-center" style={{ color: 'var(--text-secondary)' }}>
                  Stay tuned for the next market update!
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  {marketNews.length === 0 ? (
                    <p className="text-center" style={{ color: 'var(--text-secondary)' }}>
                      No market news available right now.
                    </p>
                  ) : (
                    marketNews.slice(0, 4).map((article, index) => (
                      <div key={`market-article-${index}`} className="card-3d-flat card-slide-up" ref={(el) => (cardRefs.current[22 + index] = el)}>

                        {/* Title First */}
                        <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--color-accent-blue)' }}>
                          {article.title}
                        </h3>

                        {/* Featured Image */}
                        {article.featuredImage && (
                          <div className="overflow-hidden rounded-lg bg-gray-100 mb-3 w-full">
                            <img
                              src={article.featuredImage}
                              alt={article.title}
                              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        )}

                        {/* Summary */}
                        <p className="text-secondary mb-md text-sm">
                          {article.summary || "No summary available."}
                        </p>

                        {/* Source at Bottom */}
                        <p className="text-xs" style={{ color: '#9CA3AF' }}>
                          Source:{" "}
                          <a
                            href={article.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                            style={{ color: '#9CA3AF' }}
                          >
                            {article.sourceTitle}
                          </a>
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* GUARDIAN NEWS SECTIONS - Science, Lifestyle, Food, Travel, Education */}
        {guardianLoading ? (
          /* Skeleton for Guardian sections */
          <>
            {[1, 2].map((sectionNum) => (
              <section key={sectionNum} className="section">
                <div className="section-container animate-pulse">
                  <div className="section-header">
                    <div className="h-8 w-48 rounded mb-2 mx-auto" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-4 w-64 rounded mx-auto" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-lg">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="card-3d-flat">
                        <div className="h-5 w-4/5 rounded mb-3" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                        <div className="h-48 w-full rounded-lg mb-3" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                        <div className="h-3 w-full rounded mb-2" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                        <div className="h-3 w-5/6 rounded mb-4" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                        <div className="h-3 w-24 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ))}
          </>
        ) : (
          Object.entries(GUARDIAN_SECTIONS).map(([key, config], sectionIndex) => (
          <section key={key} id={`${key}-news`} className="section">
            <div className="section-container">
              <div className="section-header section-title-slide-left" ref={(el) => (sectionRefs.current[4 + sectionIndex] = el)}>
                <h2 className="section-title">{config.title}</h2>
                <p className="section-description">{config.description}</p>
              </div>

              <div className="mt-8">
                {guardianNews[key]?.length === 0 ? (
                  <p className="text-center" style={{ color: 'var(--text-secondary)' }}>
                    No {key} news available right now.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                    {guardianNews[key]?.slice(0, 4).map((article, index) => (
                      <div key={`${key}-article-${index}`} className="card-3d-flat card-slide-up" ref={(el) => (cardRefs.current[30 + (sectionIndex * 4) + index] = el)}>

                        {/* Title First */}
                        <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--color-accent-blue)' }}>
                          {article.title}
                        </h3>

                        {/* Featured Image */}
                        {article.featuredImage && (
                          <div className="overflow-hidden rounded-lg bg-gray-100 mb-3 w-full">
                            <img
                              src={article.featuredImage}
                              alt={article.title}
                              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        )}

                        {/* Summary */}
                        <p className="text-secondary mb-md text-sm">
                          {article.summary || "No summary available."}
                        </p>

                        {/* Source at Bottom */}
                        <p className="text-xs" style={{ color: '#9CA3AF' }}>
                          Source:{" "}
                          <a
                            href={article.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                            style={{ color: '#9CA3AF' }}
                          >
                            {article.sourceTitle}
                          </a>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )))}

        {/* CTA SECTION - Full Width Flush */}
        <section className="cta-section">
          <h2 className="cta-title">Ready to Build Something Amazing?</h2>
          <p className="cta-description">
            Let's collaborate on your next cloud infrastructure project. Whether you need AI automation,
            serverless architecture, or multi-cloud solutions - I'm here to help.
          </p>
          <div className="cta-actions">
            <Link to="/contact">
              <button className="btn-cta-primary btn-lg">Get in Touch</button>
            </Link>
            <Link to="/projects">
              <button className="btn-3d-dark btn-lg">Explore Projects</button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
